import { createFileRoute, Link } from "@tanstack/react-router";
import { PropertyListView } from "@/components/public/PropertyListView";
import { useI18n } from "@/lib/i18n";
import { fetchPublicProperties } from "@/lib/publicData";
import {
  buildHead,
  listingCopy,
  pickLang,
  humanizeSlug as humanize,
  IN_LANGUAGE,
  SITE_URL,
} from "@/lib/seo";

const typeSlugToKey: Record<string, string> = {
  casas: "casa",
  departamentos: "departamento",
  terrenos: "terreno",
  oficinas: "oficina",
  locales: "local",
  duplex: "duplex",
};

export const Route = createFileRoute("/_public/$operation/$type/$location")({
  component: ListingLocationPage,
  loader: ({ params }) =>
    fetchPublicProperties({
      operation: params.operation,
      property_type: typeSlugToKey[params.type] ?? params.type,
      location_slug: params.location,
    }),
  head: ({ params, match }) => {
    const lang = pickLang(match.search);
    const copy = listingCopy(lang, params.operation, params.type, params.location);
    const path = `/${params.operation}/${params.type}/${params.location}`;
    const { meta, links, url } = buildHead({
      lang,
      path,
      title: copy.title,
      description: copy.description,
    });
    return {
      meta,
      links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: copy.title,
            description: copy.description,
            url,
            inLanguage: IN_LANGUAGE[lang],
            isPartOf: {
              "@type": "WebSite",
              name: "Dahiana Marín Bienes Raíces",
              url: SITE_URL,
            },
          }),
        },
      ],
    };
  },
});

function ListingLocationPage() {
  const { operation, type, location } = Route.useParams();
  const initial = Route.useLoaderData();
  const { lang } = useI18n();
  const copy = listingCopy(lang, operation, type, location);
  const parent = listingCopy(lang, operation, type);
  const typeKey = typeSlugToKey[type] ?? type;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <nav className="text-xs uppercase tracking-wider text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          {lang === "en" ? "Home" : lang === "pt" ? "Início" : "Inicio"}
        </Link>
        <span className="mx-2">·</span>
        <Link to="/$operation/$type" params={{ operation, type }} className="hover:text-foreground">
          {parent.h1}
        </Link>
        <span className="mx-2">·</span>
        <span>{humanize(location)}</span>
      </nav>
      <h1 className="mt-3 text-3xl font-semibold text-foreground md:text-4xl">{copy.h1}</h1>
      <p className="mt-2 text-sm text-muted-foreground md:text-base">{copy.intro}</p>

      <div className="mt-10">
        <PropertyListView
          filters={{ operation, property_type: typeKey, location_slug: location }}
          initialProperties={initial as never}
        />

      </div>


      {operation === "venta" && type === "departamentos" && location === "asuncion" && (
        <section className="mt-16 max-w-3xl">
          <h2 className="font-serif text-2xl text-foreground md:text-3xl">
            Departamentos en venta en Asunción: qué mirar antes de comprar
          </h2>
          <p className="mt-3 text-muted-foreground">
            El mercado de departamentos en venta en Asunción se concentra en el eje de Villa Morra,
            Recoleta, Carmelitas, Las Lomas y Mburucuyá, con desarrollos más nuevos hacia la zona
            corporativa y la costanera. El precio por metro cuadrado cambia bastante entre barrios y,
            dentro de un mismo barrio, según antigüedad del edificio, amenities y cantidad de
            cocheras.
          </p>
          <h3 className="mt-6 text-lg font-semibold text-foreground">Superficie propia vs. total</h3>
          <p className="mt-2 text-muted-foreground">
            Compará siempre metros propios, no la superficie total publicada: dos unidades con los
            mismos m² totales pueden tener áreas útiles muy distintas según balcones y espacios
            comunes prorrateados.
          </p>
          <h3 className="mt-6 text-lg font-semibold text-foreground">Expensas y ocupación</h3>
          <p className="mt-2 text-muted-foreground">
            Pedí el monto real de expensas de los últimos meses y el porcentaje de unidades
            ocupadas. Un edificio con amenities amplios y baja ocupación reparte los gastos entre
            menos propietarios.
          </p>
          <h3 className="mt-6 text-lg font-semibold text-foreground">Documentación</h3>
          <p className="mt-2 text-muted-foreground">
            Antes de señar, verificá el reglamento de propiedad horizontal, el título de la unidad y
            que no existan deudas de expensas ni de impuesto inmobiliario. Podés leer el detalle en
            la guía sobre{" "}
            <Link to="/blog/$slug" params={{ slug: "verificar-titulo-inmueble-paraguay" }} className="underline">
              cómo verificar el título de un inmueble
            </Link>
            .
          </p>
        </section>
      )}
    </div>
  );
}
